// Utilidades de horarios de la agenda, compartidas entre la vista Día y la vista Mes
// para que la noción de "slot ocupado" no diverja entre pantallas.

// Convierte "HH:MM" (o "H:MM") a minutos desde medianoche. Devuelve null si no se puede leer.
export function aMinutos(hhmm) {
  if (!hhmm) return null;
  const [h, m] = String(hhmm).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// Convierte minutos desde medianoche a "HH:MM".
export function aHHMM(minutos) {
  const h = Math.floor(minutos / 60);
  const m = minutos % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

// Horario de la agenda cuando el usuario nunca eligió el suyo. Es EXACTAMENTE el que la app
// tuvo siempre escrito a mano en los 4 calendarios: slots de 09:00 a 22:30.
// El cierre es EXCLUSIVO: el último turno empieza media hora antes ("hasta las 23:00" = último
// turno 22:30–23:00), que es lo que espera cualquiera al leer "mi agenda va de 8 a 20".
export const APERTURA_DEFECTO = "09:00";
export const CIERRE_DEFECTO = "23:00";

// Slots de 30 min entre apertura y cierre, MÁS la hora de cada turno recibido aunque caiga
// fuera del rango. Esa red de seguridad no es un lujo: la importación de Google Calendar crea
// turnos a cualquier hora (useGoogleCalendar toma la hora literal del evento), así que con el
// rango fijo de antes un turno a las 08:00 existía, ocupaba y NO se veía en la grilla.
// Los turnos que se pasen deben ser SOLO los del período que se está dibujando (el día, la
// semana): si se pasa la colección entera, la grilla se llena de filas de días que no se ven.
export function generarSlots(apertura, cierre, turnos = []) {
  const inicio = aMinutos(apertura) ?? aMinutos(APERTURA_DEFECTO);
  let fin = aMinutos(cierre) ?? aMinutos(CIERRE_DEFECTO);
  // Rango incoherente (guardado a mano, dato viejo): al menos un slot, nunca una lista vacía.
  if (fin <= inicio) fin = inicio + 30;

  const minutos = new Set();
  for (let m = inicio; m < fin; m += 30) minutos.add(m);
  for (const turno of turnos) {
    const m = aMinutos(turno?.inicio);
    if (m !== null) minutos.add(m);
  }
  return [...minutos].sort((a, b) => a - b).map(aHHMM);
}

// ¿Esa hora quedó fuera del horario de trabajo? Sirve para marcarla en la agenda: se muestra
// igual (hay un turno ahí), pero se avisa que está fuera de lo que el usuario configuró.
export function fueraDeHorario(hora, apertura, cierre) {
  const m = aMinutos(hora);
  const inicio = aMinutos(apertura) ?? aMinutos(APERTURA_DEFECTO);
  const fin = aMinutos(cierre) ?? aMinutos(CIERRE_DEFECTO);
  if (m === null || inicio === null || fin === null) return false;
  return m < inicio || m >= fin;
}

// Días de la semana, en el orden en que los muestra la app (lunes primero).
export const DIAS_SEMANA = [
  { clave: "lun", label: "Lun", plural: "los lunes" },
  { clave: "mar", label: "Mar", plural: "los martes" },
  { clave: "mie", label: "Mié", plural: "los miércoles" },
  { clave: "jue", label: "Jue", plural: "los jueves" },
  { clave: "vie", label: "Vie", plural: "los viernes" },
  { clave: "sab", label: "Sáb", plural: "los sábados" },
  { clave: "dom", label: "Dom", plural: "los domingos" },
];

export const TODOS_LOS_DIAS = Object.freeze({
  lun: true, mar: true, mie: true, jue: true, vie: true, sab: true, dom: true,
});

// Índice de getDay() (0 = domingo) a la clave que usa `diasActivos`.
const CLAVE_POR_INDICE = ["dom", "lun", "mar", "mie", "jue", "vie", "sab"];

// "YYYY-MM-DD" (o Date) → "lun" | "mar" | … Devuelve null si no se puede leer.
// La fecha se parte a mano a propósito: `new Date("2026-08-02")` se interpreta como UTC y en
// Uruguay (UTC-3) devuelve el día ANTERIOR, así que el domingo se leería como sábado.
export function claveDia(fecha) {
  let d;
  if (fecha instanceof Date) {
    d = fecha;
  } else {
    const [anio, mes, dia] = String(fecha ?? "").split("T")[0].split("-").map(Number);
    if (!anio || !mes || !dia) return null;
    d = new Date(anio, mes - 1, dia);
  }
  if (Number.isNaN(d.getTime())) return null;
  return CLAVE_POR_INDICE[d.getDay()];
}

// "los domingos", para armar mensajes ("No atendés los domingos").
export function diaEnPlural(fecha) {
  const clave = claveDia(fecha);
  return DIAS_SEMANA.find((d) => d.clave === clave)?.plural || "ese día";
}

// Normaliza lo guardado en el perfil. Si no hay nada, o si quedaron los 7 días apagados (dato
// viejo o a medio guardar), se atiende todos los días: la agenda nunca puede quedar sin un solo
// día visible.
export function normalizarDiasActivos(guardados) {
  if (!guardados || typeof guardados !== "object") return { ...TODOS_LOS_DIAS };
  const dias = {};
  for (const { clave } of DIAS_SEMANA) dias[clave] = guardados[clave] === true;
  return DIAS_SEMANA.some(({ clave }) => dias[clave]) ? dias : { ...TODOS_LOS_DIAS };
}

// ¿El turno CUBRE ese horario sin empezar en él?
// Una cita de 9:00 a 10:00 ocupa también las 9:30: sin esto, ese slot se mostraba como
// "Disponible" e invitaba a agendar encima (la validación de solapamiento después lo frenaba).
// Se excluye el inicio (ese slot se trata aparte, es el que dibuja la tarjeta) y el fin
// (una cita de 9 a 10 deja las 10:00 libres).
export function turnoCubre(turno, hora) {
  const inicio = aMinutos(turno?.inicio);
  const fin = aMinutos(turno?.fin);
  const h = aMinutos(hora);
  if (inicio === null || fin === null || h === null) return false;
  return h > inicio && h < fin;
}
